/**
 * UserAssignSelect — F1 Processor / Sales Rep selector.
 *
 * Wraps shadcn Select with a "Unassigned" option (sentinel value "__none"
 * to avoid Radix's "empty value" restriction). Caller receives either a
 * full user object (id/name/email/role) or null on clear.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const NONE = "__none";

export default function UserAssignSelect({
    value,             // current selected user id (string | null)
    onSelect,          // (user|null) => void
    placeholder = "Select…",
    testId,
    allowUnassigned = true,
    triggerClassName = "h-8 text-xs w-[200px]",
}) {
    const [users, setUsers] = useState([]);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const { data } = await api.get("/users/staff");
                if (!alive) return;
                setUsers(Array.isArray(data) ? data : []);
            } catch (e) {
                // Silent — selector simply stays empty + the field shows
                // the existing name if any. Toast would be noisy here.
                // eslint-disable-next-line no-console
                console.warn("UserAssignSelect load failed:", formatApiError(e));
            } finally {
                if (alive) setLoaded(true);
            }
        })();
        return () => { alive = false; };
    }, []);

    const selected = value || NONE;

    const handleChange = (v) => {
        if (v === NONE) return onSelect?.(null);
        const u = users.find(x => x.id === v);
        if (u) onSelect?.(u);
    };

    return (
        <Select value={selected} onValueChange={handleChange}>
            <SelectTrigger className={triggerClassName} data-testid={testId}>
                <SelectValue placeholder={loaded ? placeholder : "Loading…"} />
            </SelectTrigger>
            <SelectContent>
                {allowUnassigned && <SelectItem value={NONE}>Unassigned</SelectItem>}
                {users.map(u => (
                    <SelectItem key={u.id} value={u.id}>
                        {u.name} <span className="text-muted-foreground">— {u.email}</span>
                    </SelectItem>
                ))}
            </SelectContent>
        </Select>
    );
}
